import { useEffect, useState } from 'react';

import MarketingHeader from '@/pages/(marketing)/index/components/Header';
import useMe, { hasLikelyValidToken } from '@/renderer/utils/useMe';

/**
 * Standard chrome for public, shareable "in-app" content landings (e.g. /order,
 * /app). Drop it at the very top of such a page's +Page.tsx.
 *
 * The layout convention for these routes is header-vs-sidebar-by-auth:
 *   • Signed-out visitor → marketing header (this component) + footer, so they
 *     always have a way to navigate.
 *   • Signed-in user     → the app sidebar (renderer/Nav) carries navigation, so
 *     the marketing header is suppressed to avoid a redundant double header.
 *
 * This component owns the header half. Nav (renderer/Nav.tsx) owns the sidebar
 * half: it hides the sidebar for signed-out visitors on these routes (see
 * MARKETING_LANDING_ROUTES there) and shows it for signed-in users.
 *
 * New public landings should reuse this component instead of rendering a bare
 * logo and instead of adding the route to Nav's HIDE_ON list — HIDE_ON removes
 * the sidebar for *everyone*, which strands signed-in app users with no
 * navigation (the /app regression, issue #1405).
 */
export default function MarketingHeaderSlot() {
  // The signed-in user from RasContext, the same value Nav uses to decide
  // whether to show the sidebar/tab bar. Read reactively, so the header goes
  // away as soon as auth resolves.
  const user = useMe();

  // Starts false so SSR and the first client render agree (header shown), then
  // resolves after mount — the same post-hydration pattern the sidebar uses.
  const [mounted, setMounted] = useState(false);
  const [likelyLoggedIn, setLikelyLoggedIn] = useState(false);
  useEffect(() => {
    setMounted(true);
    setLikelyLoggedIn(hasLikelyValidToken());
  }, []);

  // The token hint alone is not enough: it is read once at mount and is false
  // whenever the short-lived access token has lapsed but the session is still
  // good (it refreshes a moment later). Signed-in members then got the dark
  // marketing header stacked on top of the app's tab bar on /order (#2142).
  // A resolved user always wins, so signed-in members only ever get app chrome.
  if (mounted && (likelyLoggedIn || user?.role)) {
    return null;
  }

  // Before mount, the header is also hidden by CSS when the head's pre-paint
  // script found a likely-valid token (<html data-auth="likely">), so a
  // signed-in user never sees it flash (renderer/utils/authHint.ts). After
  // mount React is the source of truth: `data-resolved` lifts that CSS rule, so
  // a visitor whose token turned out to be stale still gets the header.
  return (
    <div data-marketing-header-slot="" data-resolved={mounted ? '' : undefined}>
      <MarketingHeader absolute={false} />
    </div>
  );
}
